import { useRef, useContext, useLayoutEffect, KeyboardEvent } from "react";
import { TooltipContext } from "./TooltipContext";

export type dataStateType = "open" | "closed";

/**
 * Custom hook for managing tooltip context and interactions.
 *
 * Features:
 * - Manages tooltip trigger and content refs
 * - Handles mouse and keyboard interactions
 * - Provides ARIA attributes for accessibility
 * - Manages controlled and uncontrolled states
 * - Handles tooltip positioning and visibility
 * - Provides event handlers for trigger and content
 * - Manages arrow element positioning
 *
 * @returns Object containing tooltip context, refs, and event handlers
 */
export function useTooltipContext() {
  const context = useContext(TooltipContext);
  const invokerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!invokerRef.current) return;
    context?.setInvoker?.(invokerRef.current);
    if (!context?.openState) return;
    context.openTooltip();
  }, [context, invokerRef]);

  useLayoutEffect(() => {
    if (!popoverRef.current) return;
    context?.setTooltip?.(popoverRef.current);
    context?.setRootNode?.(popoverRef.current.getRootNode() as HTMLElement);
    if (!arrowRef.current) return;
    context?.setArrowElement?.(arrowRef.current);
  }, [context, popoverRef]);

  const onMouseEnterHandler = () => {
    if (context?.controlled) return;
    context?.openTooltip();
  };
  const onMouseLeaveHandler = () => {
    if (context?.controlled) return;
    context?.closeTooltip();
  };

  const onFocusHandler = () => {
    if (context?.controlled) return;
    context?.openTooltip();
  };
  const onBlurHandler = () => {
    if (context?.controlled) return;
    context?.closeTooltip();
  };

  const onKeyDownHandler = (e: KeyboardEvent) => {
    if (!context?.openState) return;
    if (e.code === "Escape" && !context.controlled) {
      context?.closeTooltip();
      return;
    }
  };

  if (!context || !invokerRef || !popoverRef) return;
  return {
    invoker: {
      ref: invokerRef,
      element: context.invoker,
      onMouseEnter: onMouseEnterHandler,
      onMouseLeave: onMouseLeaveHandler,
      onKeyDown: onKeyDownHandler,
      onFocus: onFocusHandler,
      onBlur: onBlurHandler,
      "aria-haspopup": true,
      "aria-details": context.tooltipId,
      "data-state": context.openState ? "open" : ("closed" as dataStateType),
      "aria-describedby": context.tooltipId,
    },
    tooltip: {
      ref: popoverRef,
      element: context.popover,
      id: context.tooltipId,
      setId: context.setTooltipId,
      show: context.openTooltip,
      close: context.closeTooltip,
      style: context.tooltipStyle,
      arrowRef: arrowRef,
      key: context.resetKey,
    },
    setOpen: context.setOpenState,
    open: context.openState,
    controlled: context.controlled,
    placement: context.placement,
    disableFlipFallback: context.disableFlipFallback,
  };
}
